import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '../../components/ui/Card'
import { Select } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { projectsApi } from '../../api/projects.api'
import { rolesApi } from '../../api/roles.api'
import { tasksApi } from '../../api/tasks.api'
import { TaskListView } from './shared/TaskListView'
import { TaskCalendarView } from './shared/TaskCalendarView'
import { TaskDetailModal } from './detail/TaskDetailModal'
import { PRIORITY_OPTIONS } from './shared/PriorityBadge'
import type { TaskPriority } from '../../api/types'
import styles from '../projects/ProjectDetail.module.css'

type ViewMode = 'list' | 'calendar'

export function TasksOverviewPage() {
  const [mine, setMine] = useState(true)
  const [projectId, setProjectId] = useState('')
  const [roleId, setRoleId] = useState('')
  const [priority, setPriority] = useState<TaskPriority | ''>('')
  const [view, setView] = useState<ViewMode>('list')
  const [openTaskId, setOpenTaskId] = useState<string | null>(null)

  const projectsQuery = useQuery({ queryKey: ['projects'], queryFn: async () => (await projectsApi.list({ pageSize: 100 })).data })
  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: async () => (await rolesApi.list()).data })

  const tasksQuery = useQuery({
    queryKey: ['tasks', 'overview', { mine, projectId, roleId, priority }],
    queryFn: async () =>
      (
        await tasksApi.list({
          mine: mine || undefined,
          projectId: projectId || undefined,
          assigneeRoleId: roleId || undefined,
          priority: priority || undefined,
          pageSize: 500,
        })
      ).data,
  })

  const tasks = tasksQuery.data ?? []

  return (
    <div>
      <PageHeader title="Aufgaben" subtitle="Projektübergreifende Sicht auf alle Aufgaben, für die du berechtigt bist." />

      <div className={styles.tabs}>
        <button className={[styles.tab, view === 'list' ? styles.tabActive : ''].join(' ')} onClick={() => setView('list')}>
          Liste
        </button>
        <button className={[styles.tab, view === 'calendar' ? styles.tabActive : ''].join(' ')} onClick={() => setView('calendar')}>
          Kalender
        </button>
      </div>

      <div className={styles.filterBar}>
        <Button variant={mine ? 'primary' : 'secondary'} size="sm" onClick={() => setMine((v) => !v)}>
          Meine Aufgaben
        </Button>
        <Select className={styles.filterSelect} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
          <option value="">Alle Projekte</option>
          {projectsQuery.data?.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        <Select className={styles.filterSelect} value={roleId} onChange={(e) => setRoleId(e.target.value)}>
          <option value="">Alle Rollen</option>
          {rolesQuery.data?.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </Select>
        <Select className={styles.filterSelect} value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority | '')}>
          <option value="">Alle Prioritäten</option>
          {PRIORITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      </div>

      {view === 'list' ? <TaskListView tasks={tasks} onOpenTask={setOpenTaskId} /> : <TaskCalendarView tasks={tasks} onOpenTask={setOpenTaskId} />}

      {openTaskId ? <TaskDetailModal taskId={openTaskId} onClose={() => setOpenTaskId(null)} onOpenTask={setOpenTaskId} /> : null}
    </div>
  )
}
